<template>
  <nav class="h-100 flex-grow-1">
    <div class="d-flex flex-column flex-shrink-0 h-100">
      <div class="px-3 pt-3">
        <RouterLink to="/" class="d-flex align-items-center me-md-auto link-body-emphasis text-decoration-none">
          <div class="logo">
            <img alt="Logo" src="/logo.svg" class="logo"/>
          </div>
        </RouterLink>
      </div>
      <div class="my-3 d-grid">
        <RouterLink class="btn btn-primary-subtle bg-primary-subtle border-0 rounded-0 px-2 py-2 text-left"
                    style="text-align:left;" to="/account/site" v-if="isAuthenticated && userTenants.length > 1">
          <font-awesome-layers class="">
            <font-awesome-icon :icon="['far', 'building']"/>
          </font-awesome-layers>
          <span v-if="!!selectedTenantName" class="text-truncate ms-1">{{ selectedTenantName }}</span>
          <span v-if="!selectedTenantName" class="text-truncate ms-1 fst-italic">--- {{ $t('menu.tenant') }} ---</span>
        </RouterLink>
        <RouterLink class="btn btn-primary-subtle bg-primary-subtle border-0 rounded-0 px-2 py-2 text-left"
                    style="text-align:left;" to="/account/site" v-if="isAuthenticated">
          <font-awesome-layers class="">
            <font-awesome-icon :icon="['far', 'warehouse']"/>
          </font-awesome-layers>
          <span v-if="!!selectedSiteName" class="text-truncate ms-1">{{ selectedSiteName }}</span>
          <span v-if="!selectedSiteName" class="text-truncate ms-1 fst-italic">--- {{ $t('menu.site') }} ---</span>
        </RouterLink>
      </div>
      <div class="d-flex flex-column flex-grow-1 px-1">
        <ul class="nav nav-pills flex-column mb-auto">
          <template v-if="isAuthenticated && selectedSiteId > 0">
            <li>
              <RouterLink class="nav-link" :to="TranslationsService.i18nRoute({ name: 'consumption-create' })" :class="{ 'active': route.path.includes('/transaction/consumption') }">
                <div class="d-flex align-items-center">
                  <div class="flex-grow-1">
                    <font-awesome-icon :icon="['far', 'clipboard-medical']"/>
                    <span class="ms-1 text">{{ $t('menu.consumption') }}</span>
                  </div>
                  <div>
                    <span class="border-end mx-2"></span>
                  </div>
                  <a 
                    class="nav-link nav-link-embedded-button" 
                    :class="{ 'active': route.path.includes('/transaction/consumption') }"
                    :title="$t('menu.consumption-list')"
                    @click.stop.prevent="router.push(TranslationsService.i18nRoute({ name: 'consumptions' }))"
                  >
                    <font-awesome-icon :icon="['far', 'table-list']"/>
                  </a>
                </div>
              </RouterLink>
            </li>
            <li>
              <RouterLink class="nav-link" :to="TranslationsService.i18nRoute({ name: 'order-create' })" :class="{ 'active': route.path.includes('/transaction/orders') }">
                <div class="d-flex align-items-center">
                  <div class="flex-grow-1">
                    <font-awesome-icon :icon="['far', 'cart-shopping']"/>
                    <span class="ms-1 text">{{ $t('menu.orders') }}</span>
                  </div>
                  <div>
                    <span class="border-end mx-2"></span>
                  </div>
                  <a 
                    class="nav-link nav-link-embedded-button" 
                    :class="{ 'active': route.path.includes('/transaction/orders') }"
                    :title="$t('menu.order-list')"
                    @click.stop.prevent="router.push(TranslationsService.i18nRoute({ name: 'orders' }))"
                  >
                    <font-awesome-icon :icon="['far', 'table-list']"/>
                  </a>
                </div>
              </RouterLink>
            </li>
            <li>
              <RouterLink class="nav-link" :to="TranslationsService.i18nRoute({ name: 'receivings' })">
                <font-awesome-icon :icon="['far', 'clipboard-list-check']" />
                <span class="ms-1 text">{{ $t('menu.receivings') }}</span>
              </RouterLink>
            </li>
            <li>
              <RouterLink class="nav-link" :to="TranslationsService.i18nRoute({ name: 'deliveries' })">
                <font-awesome-icon :icon="['far', 'truck']"/>
                <span class="ms-1 text">{{ $t('menu.deliveries') }}</span>
              </RouterLink>
            </li>
            <li class="border-top my-3 d-none"></li>
            <li>
              <RouterLink class="nav-link" :to="TranslationsService.i18nRoute({ name: 'stock-receipt' })">
                <font-awesome-icon :icon="['fas', 'plus']" />
                <span class="ms-1 text">{{ $t('menu.stock-receipt') }}</span>
              </RouterLink>
            </li>
            <li>
              <RouterLink class="nav-link" :to="TranslationsService.i18nRoute({ name: 'stock-issue' })">
                <font-awesome-icon :icon="['fas', 'minus']" />
                <span class="ms-1 text">{{ $t('menu.stock-issue') }}</span>
              </RouterLink>
            </li>
            <div class="mel-divider mel-divider-success d-flex align-items-center mt-2 mb-1"><p class="text-center fw-bold mx-3 mb-0 text-secondary">{{ $t('menu.controlling') }}</p></div>
            <li>
              <RouterLink class="nav-link" :to="TranslationsService.i18nRoute({ name: 'info' })">
                <font-awesome-icon :icon="['far', 'square-info']"/>
                <span class="ms-1 text">{{ $t('menu.info-request') }}</span>
              </RouterLink>
            </li>
            <li>
              <RouterLink class="nav-link" :to="TranslationsService.i18nRoute({ name: 'stocks' })">
                <font-awesome-icon :icon="['far', 'boxes-stacked']"/>
                <span class="ms-1 text">{{ $t('menu.stocks') }}</span>
              </RouterLink>
            </li>
            <li>
              <RouterLink class="nav-link" :to="TranslationsService.i18nRoute({ name: 'stock-changes' })">
                <font-awesome-icon :icon="['fas', 'plus-minus']"/>
                <span class="ms-1 text">{{ $t('menu.stock-changes') }}</span>
              </RouterLink>
            </li>
            <li>
              <RouterLink class="nav-link" :to="TranslationsService.i18nRoute({ name: 'stock-demand' })">
                <font-awesome-icon :icon="['fas', 'shelves']"/>
                <span class="ms-1 text">{{ $t('menu.stock-demand') }}</span>
              </RouterLink>
            </li>
            <li>
              <RouterLink class="nav-link" :to="TranslationsService.i18nRoute({ name: 'order-demand' })">
                <font-awesome-layers>
                  <font-awesome-icon :icon="['far', 'cart-shopping']" transform="up-4 right-4 shrink-4" />
                  <font-awesome-icon :icon="['fas', 'shelves']" transform="down-4 left-4 shrink-4" />
                </font-awesome-layers>
                <span class="ms-1 text">{{ $t('menu.order-demand') }}</span>
              </RouterLink>
            </li>
            <li class="border-top my-3 d-none"></li>
            <li>
              <RouterLink class="nav-link" :to="TranslationsService.i18nRoute({ name: 'biExpiry' })">
                <font-awesome-icon :icon="['far', 'hourglass-clock']"/>
                <span class="ms-1 text">{{ $t('menu.statistics.expiry') }}</span>
              </RouterLink>
            </li>
            <li>
              <RouterLink class="nav-link" :to="TranslationsService.i18nRoute({ name: 'biItemIssues' })">
                <font-awesome-icon :icon="['far', 'chart-simple-horizontal']"/>
                <span class="ms-1 text">{{ $t('menu.statistics.consumption') }}</span>
              </RouterLink>
            </li>
            <div class="mel-divider mel-divider-secondary d-flex align-items-center mt-2 mb-1"><p class="text-center fw-bold mx-3 mb-0 text-secondary">{{ $t('menu.masterdata') }}</p></div>
            <li>
              <RouterLink class="nav-link" :to="TranslationsService.i18nRoute({ name: 'items' })">
                <font-awesome-icon :icon="['far', 'rectangle-list']"/>
                <span class="ms-1 text">{{ $t('menu.items') }}</span>
              </RouterLink>
            </li>
            <li>
              <RouterLink class="nav-link" :to="TranslationsService.i18nRoute({ name: 'itemblocks' })">
                <font-awesome-icon :icon="['far', 'kit-medical']"/>
                <span class="ms-1 text">{{ $t('menu.itemblocks') }}</span>
              </RouterLink>
            </li>
            <li>
              <RouterLink class="nav-link" :to="TranslationsService.i18nRoute({ name: 'suppliers' })">
                <font-awesome-icon :icon="['far', 'person-dolly']"/>
                <span class="ms-1 text">{{ $t('menu.suppliers') }}</span>
              </RouterLink>
            </li>
            <li>
              <RouterLink class="nav-link" :to="TranslationsService.i18nRoute({ name: 'foreign-items' })">
                <font-awesome-layers>
                  <font-awesome-icon :icon="['far', 'rectangle-list']" transform="up-4 right-4 shrink-4" />
                  <font-awesome-icon :icon="['fas', 'person-dolly']" transform="down-4 left-4 shrink-4" />
                </font-awesome-layers>
                <span class="ms-1 text">{{ $t('menu.foreign-items') }}</span>
              </RouterLink>
            </li>
            <li>
              <RouterLink class="nav-link" :to="TranslationsService.i18nRoute({ name: 'labels-item' })">
                <font-awesome-icon :icon="['far', 'qrcode']"/>
                <span class="ms-1 text">{{ $t('menu.label-item') }}</span>
              </RouterLink>
            </li>
            <li>
              <RouterLink class="nav-link" :to="TranslationsService.i18nRoute({ name: 'labels-box' })">
                <font-awesome-icon :icon="['far', 'tag']"/>
                <span class="ms-1 text">{{ $t('menu.label-box') }}</span>
              </RouterLink>
            </li>
            <li class="border-top my-3 d-none"></li>
            <li>
              <RouterLink class="nav-link" :to="TranslationsService.i18nRoute({ name: 'label-printers' })">
                <font-awesome-icon :icon="['far', 'print']"/>
                <span class="ms-1 text">{{ $t('menu.label-printers') }}</span>
              </RouterLink>
            </li>
            <li>
              <RouterLink class="nav-link" :to="TranslationsService.i18nRoute({ name: 'label-templates' })">
                <font-awesome-icon :icon="['far', 'file-code']"/>
                <span class="ms-1 text">{{ $t('menu.label-templates') }}</span>
              </RouterLink>
            </li>
          </template>
        </ul>
        <div v-if="isAuthenticated">
          <ul class="nav nav-pills flex-column mb-auto">
          <template v-if="isAuthenticated && (isSystemAdmin || isTenantAdmin || isSiteAdmin)">
            <div class="mel-divider mel-divider-secondary d-flex align-items-center mt-2 mb-1"><p class="text-center fw-bold mx-3 mb-0 text-secondary">{{ $t('menu.administration') }}</p></div>
            <li>
              <RouterLink class="nav-link" :to="TranslationsService.i18nRoute({ name: 'manage-tenants' })">
                <font-awesome-icon :icon="['far', 'building-memo']"/>
                <span class="ms-1 text">{{ $t('menu.manage-tenants') }}</span>
              </RouterLink>
            </li>
            <li>
              <RouterLink class="nav-link" :to="TranslationsService.i18nRoute({ name: 'manage-users' })">
                <font-awesome-icon :icon="['far', 'users-gear']"/>
                <span class="ms-1 text">{{ $t('menu.manage-users') }}</span>
              </RouterLink>
            </li>
            <li v-if="isSystemAdmin">
              <RouterLink class="nav-link" :to="TranslationsService.i18nRoute({ name: 'api-request-logs' })">
                <font-awesome-icon :icon="['far', 'rectangle-list']"/>
                <span class="ms-1 text">API Request Log</span>
              </RouterLink>
            </li>
          </template>
          <li class="border-top my-3"></li>
          <li>
            <div class="d-flex justify-content-between pb-1">
              <RouterLink class="nav-link" :to="TranslationsService.i18nRoute({ name: 'profile' })">
                <font-awesome-icon :icon="['fas', 'id-card']" />
                <span class="ms-1 text">{{ authUser?.name ?? authUser?.email }}</span>
              </RouterLink>
              <a @click="logoutUser()" class="nav-link" href="#" title="Log out">
                <font-awesome-icon :icon="['fas', 'arrow-right-from-bracket']" />
              </a>
            </div>
          </li>
        </ul>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { logoutUser } from '@/services/auth';
import { useAuthStore, useSessionStore } from '@/stores';
import TranslationsService from "@/services/translations.ts";

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const sessionStore = useSessionStore();

const { user: authUser, isAuthenticated } = storeToRefs(authStore);
const { userTenants, isSystemAdmin, isTenantAdmin, isSiteAdmin, selectedSiteId, selectedTenant, selectedSite } = storeToRefs(sessionStore);

const selectedTenantName = computed(() => selectedTenant.value?.name ?? '');
const selectedSiteName = computed(() => selectedSite.value?.name ?? '');
</script>

<style lang="scss" scoped>
a.nav-link,
div.nav-link {
  padding-left: 0.75rem;
  padding-right: 0.75rem;

  &.nav-link-embedded-button {
    height: 100%;
    min-height: inherit;
    display: flex;
    align-items: center;
    padding: 10px 4px;
    margin: -10px -10px -10px -8px;
  }
}
</style>